import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import { REQUEST_URL } from '../config/urls';

const primaryColor = '#c41e3a';
const primaryGradient = 'linear-gradient(135deg, #c41e3a 0%, #a01830 100%)';

// Adiciona estilos CSS responsivos
const responsiveStyles = `
  @media (max-width: 768px) {
    .responsive-main-grid {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
    }

    .responsive-title {
      font-size: 32px !important;
      letter-spacing: -1px !important;
    }

    .responsive-subtitle {
      font-size: 15px !important;
    }

    .responsive-process-steps {
      grid-template-columns: 1fr !important;
      gap: 20px !important;
    }

    .responsive-advantages {
      grid-template-columns: 1fr !important;
    }

    .responsive-stats-grid {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
    }

    .responsive-process-section {
      padding: 24px 20px !important;
    }

    .responsive-process-title {
      font-size: 20px !important;
      margin-bottom: 20px !important;
    }

    .responsive-button {
      padding: 14px 24px !important;
      font-size: 15px !important;
    }

    .responsive-feature-item {
      font-size: 14px !important;
    }

    .responsive-info-card {
      padding: 20px !important;
    }

    .responsive-stat-number {
      font-size: 28px !important;
    }

    .responsive-page {
      padding: 40px 16px 24px !important;
    }
  }

  @media (max-width: 480px) {
    .responsive-title {
      font-size: 28px !important;
    }

    .responsive-subtitle {
      font-size: 14px !important;
    }

    .responsive-process-title {
      font-size: 18px !important;
    }

    .responsive-stat-number {
      font-size: 24px !important;
    }

    .responsive-button {
      padding: 12px 20px !important;
      font-size: 14px !important;
    }
  }
`;

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #fff5f5 100%)',
    display: 'flex',
    flexDirection: 'column',
    padding: '60px 20px 40px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    position: 'relative',
    overflowX: 'hidden',
    width: '100%',
    boxSizing: 'border-box'
  },
  backgroundPattern: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, rgba(196, 30, 58, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(196, 30, 58, 0.03) 0%, transparent 50%),
      linear-gradient(to right, #f8f9fa 1px, transparent 1px),
      linear-gradient(to bottom, #f8f9fa 1px, transparent 1px)
    `,
    backgroundSize: 'auto, auto, 40px 40px, 40px 40px',
    opacity: 0.6,
    zIndex: 0,
    pointerEvents: 'none'
  },
  container: {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    position: 'relative',
    zIndex: 1,
    boxSizing: 'border-box'
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
    alignItems: 'start',
    width: '100%',
    boxSizing: 'border-box'
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    minWidth: 0
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 14px',
    borderRadius: 8,
    background: '#fff5f5',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: primaryColor,
    alignSelf: 'flex-start',
    border: `1px solid ${primaryColor}20`
  },
  title: {
    fontSize: 44,
    fontWeight: 800,
    color: '#1a1a1a',
    letterSpacing: '-1.5px',
    margin: 0,
    lineHeight: 1.1
  },
  subtitle: {
    fontSize: 17,
    lineHeight: 1.6,
    color: '#666',
    margin: 0,
    maxWidth: '520px'
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '4px'
  },
  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '16px 32px',
    borderRadius: 12,
    background: primaryGradient,
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(196, 30, 58, 0.3)',
    transition: 'all 0.3s ease',
    letterSpacing: '0.3px'
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '4px'
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: 15,
    color: '#444'
  },
  featureIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: '#fff5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: primaryColor,
    fontSize: 14,
    flexShrink: 0
  },
  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0
  },
  infoCard: {
    background: '#fff',
    borderRadius: 16,
    padding: '24px',
    border: '1px solid #e8ecef',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease'
  },
  infoCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  infoCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: primaryGradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 16,
    boxShadow: '0 4px 12px rgba(196, 30, 58, 0.25)'
  },
  infoCardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a1a1a',
    margin: 0
  },
  infoCardText: {
    fontSize: 14,
    lineHeight: 1.7,
    color: '#666',
    margin: 0
  },
  supportBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: 10,
    background: '#f8f9fa',
    fontSize: 13,
    color: '#666',
    border: '1px solid #e8ecef',
    marginTop: '8px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginTop: '8px'
  },
  statCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '20px',
    border: '1px solid #e8ecef',
    textAlign: 'center',
    transition: 'all 0.3s ease'
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 800,
    background: primaryGradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '4px'
  },
  statLabel: {
    fontSize: 13,
    color: '#666',
    fontWeight: 500
  },
  processSection: {
    background: '#fff',
    borderRadius: 16,
    padding: '32px',
    border: '1px solid #e8ecef',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
    width: '100%',
    boxSizing: 'border-box'
  },
  processTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '24px',
    textAlign: 'center'
  },
  processSteps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px'
  },
  processStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
    position: 'relative'
  },
  processStepIcon: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: primaryGradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 24,
    boxShadow: '0 4px 16px rgba(196, 30, 58, 0.3)',
    position: 'relative',
    zIndex: 2
  },
  processStepNumber: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#1a1a1a',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    border: '3px solid #fff',
    zIndex: 3
  },
  processStepTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a1a',
    lineHeight: 1.3
  },
  processStepDesc: {
    fontSize: 12,
    color: '#666',
    lineHeight: 1.4
  },
  advantagesSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  },
  advantageCard: {
    background: 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
    borderRadius: 12,
    padding: '20px',
    border: '1px solid #e8ecef',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    transition: 'all 0.3s ease'
  },
  advantageIcon: {
    width: 48,
    height: 48,
    borderRadius: 10,
    background: primaryGradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 20,
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(196, 30, 58, 0.25)'
  },
  advantageContent: {
    flex: 1
  },
  advantageTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '6px'
  },
  advantageDesc: {
    fontSize: 13,
    color: '#666',
    lineHeight: 1.5,
    margin: 0
  },
  decorativeCircle: {
    position: 'fixed',
    borderRadius: '50%',
    background: primaryGradient,
    opacity: 0.05,
    pointerEvents: 'none',
    zIndex: 0
  }
};

class PersonalizacaoConvite extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      carregando: false
    };
  }

  handleAcessarPersonalizacao = async () => {
    const { usuario } = this.props;

    if (!usuario?.email || !usuario?.nome) {
      toast.error('Dados do usuário não encontrados');
      return;
    }

    try {
      this.setState({ carregando: true });
      const response = await fetch(`${REQUEST_URL}/personalizacao-sso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: usuario.email,
          nome: usuario.nome
        })
      });

      const data = await response.json();

      if (response.ok && data.linkSSO) {
        // Limpa cookies da Print-One antes de abrir o SSO
        // Remove todos os cookies do domínio lojaalphaconvites.com.br
        document.cookie.split(";").forEach((c) => {
          const cookie = c.trim();
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
          // Tenta limpar para o domínio atual
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
          // Tenta limpar para o domínio da Print-One
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.lojaalphaconvites.com.br";
        });

        // Abre em nova aba com o link autenticado após 100ms
        setTimeout(() => {
          window.open(data.linkSSO, '_blank', 'noopener,noreferrer');
          this.setState({ carregando: false });
        }, 100);
      } else {
        toast.error(data.error || 'Erro ao gerar link de acesso');
        console.error('Erro SSO:', data);
        this.setState({ carregando: false });
      }
    } catch (error) {
      console.error('Erro ao conectar com o servidor:', error);
      toast.error('Erro ao conectar com o sistema. Tente novamente.');
      this.setState({ carregando: false });
    }
  };

  handleAcessarPersonalizacaoGoogle = () => {
    this.handleAcessarPersonalizacao();
  };

  handleAcessarPersonalizacaoTradicional = () => {
    this.handleAcessarPersonalizacao();
  };


  render() {
    // Redireciona para a página inicial se a personalização não está liberada
    if (!this.props.contrato?.personalizacaoLiberada) {
      return <Redirect to="/" />;
    }

    return (
      <div style={styles.page} className="responsive-page">
        <style>{responsiveStyles}</style>
        <div style={styles.backgroundPattern}></div>

        {/* Decorative circles */}
        <div style={{...styles.decorativeCircle, width: 300, height: 300, top: -100, right: -100}}></div>
        <div style={{...styles.decorativeCircle, width: 200, height: 200, bottom: -50, left: -50}}></div>

        <div style={styles.container}>
          {/* Main Grid - Hero Section */}
          <div style={styles.mainGrid} className="responsive-main-grid">
            {/* Sessão da Esquerda */}
            <div style={styles.leftSection}>
              <div style={styles.header}>
                <h1 style={styles.title} className="responsive-title">
                  Sistema de Personalização
                </h1>

                <p style={styles.subtitle} className="responsive-subtitle">
                  Acesse o sistema de personalização e comece a criar seus convites de forma intuitiva e profissional. Transforme suas ideias em realidade com nossa plataforma completa.
                </p>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  onClick={this.handleAcessarPersonalizacaoGoogle}
                  disabled={this.state.carregando}
                  className="responsive-button"
                  style={{
                    ...styles.ctaButton,
                    opacity: this.state.carregando ? 0.7 : 1,
                    cursor: this.state.carregando ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!this.state.carregando) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 12px 28px rgba(196, 30, 58, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!this.state.carregando) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(196, 30, 58, 0.3)';
                    }
                  }}
                >
                  {this.state.carregando ? (
                    <>
                      <i className='fa fa-spinner fa-spin'></i>
                      Carregando...
                    </>
                  ) : (
                    <>
                      <i className='fa fa-magic'></i>
                      Acessar Personalização
                    </>
                  )}
                </button>
              </div>

              <div style={styles.featuresList}>
                <div style={styles.featureItem} className="responsive-feature-item">
                  <div style={styles.featureIcon}>
                    <i className='fa fa-check-circle'></i>
                  </div>
                  <span>Personalize facilmente</span>
                </div>
                <div style={styles.featureItem} className="responsive-feature-item">
                  <div style={styles.featureIcon}>
                    <i className='fa fa-check-circle'></i>
                  </div>
                  <span>Biblioteca visual para organizar suas fotos</span>
                </div>
                <div style={styles.featureItem} className="responsive-feature-item">
                  <div style={styles.featureIcon}>
                    <i className='fa fa-check-circle'></i>
                  </div>
                  <span>Visualize o resultado final em tempo real</span>
                </div>
              </div>

            </div>

            {/* Sessão da Direita */}
            <div style={styles.rightSection}>
              <div
                style={styles.infoCard}
                className="responsive-info-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={styles.infoCardHeader}>
                  <div style={styles.infoCardIcon}>
                    <i className='fa fa-info-circle'></i>
                  </div>
                  <h3 style={styles.infoCardTitle}>Dica Importante</h3>
                </div>
                <p style={styles.infoCardText}>
                  Reserve alguns minutos para revisar textos, datas e ortografia antes de enviar para aprovação. Uma revisão cuidadosa evita retrabalho.
                </p>
              </div>

              <div
                style={styles.infoCard}
                className="responsive-info-card"
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={styles.infoCardHeader}>
                  <div style={styles.infoCardIcon}>
                    <i className='fa fa-headphones'></i>
                  </div>
                  <h3 style={styles.infoCardTitle}>Precisa de Ajuda?</h3>
                </div>
                <p style={styles.infoCardText}>
                  Em caso de dúvidas ou ajustes específicos, fale com sua atendente no horário comercial ou com nosso atendimento virtual. Estamos aqui para ajudar!
                </p>
                <div style={styles.supportBadge}>
                  <i className='fa fa-clock-o'></i>
                  Segunda a Sexta: 7:30h às 17:50h
                </div>
              </div>

              <div style={styles.advantagesSection} className="responsive-advantages">
                <div
                  style={styles.advantageCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.advantageIcon}>
                    <i className='fa fa-bolt'></i>
                  </div>
                  <div style={styles.advantageContent}>
                    <div style={styles.advantageTitle}>Rápido e Fácil</div>
                    <p style={styles.advantageDesc}>Interface intuitiva que permite criar em minutos</p>
                  </div>
                </div>

                <div
                  style={styles.advantageCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.advantageIcon}>
                    <i className='fa fa-eye'></i>
                  </div>
                  <div style={styles.advantageContent}>
                    <div style={styles.advantageTitle}>Prévia em Tempo Real</div>
                    <p style={styles.advantageDesc}>Veja o resultado enquanto edita</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Passo a Passo */}
          <div style={styles.processSection} className="responsive-process-section">
            <h2 style={styles.processTitle} className="responsive-process-title">Como Funciona</h2>
            <div style={styles.processSteps} className="responsive-process-steps">
              <div style={styles.processStep}>
                <div style={styles.processStepIcon}>
                  <i className='fa fa-mouse-pointer'></i>
                  <div style={styles.processStepNumber}>1</div>
                </div>
                <div style={styles.processStepTitle}>Acesse o Sistema</div>
                <div style={styles.processStepDesc}>Clique no botão e entre na plataforma</div>
              </div>

              <div style={styles.processStep}>
                <div style={styles.processStepIcon}>
                  <i className='fa fa-edit'></i>
                  <div style={styles.processStepNumber}>2</div>
                </div>
                <div style={styles.processStepTitle}>Personalize</div>
                <div style={styles.processStepDesc}>Adicione textos, fotos e escolha o design</div>
              </div>

              <div style={styles.processStep}>
                <div style={styles.processStepIcon}>
                  <i className='fa fa-search'></i>
                  <div style={styles.processStepNumber}>3</div>
                </div>
                <div style={styles.processStepTitle}>Revise</div>
                <div style={styles.processStepDesc}>Confira todos os detalhes com atenção</div>
              </div>

              <div style={styles.processStep}>
                <div style={styles.processStepIcon}>
                  <i className='fa fa-check'></i>
                  <div style={styles.processStepNumber}>4</div>
                </div>
                <div style={styles.processStepTitle}>Aprove</div>
                <div style={styles.processStepDesc}>Envie para aprovação final</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    usuario: state.usuario.autenticado,
    contrato: state.contrato.especifico,
  };
};

export default connect(mapStateToProps)(PersonalizacaoConvite);
